import type { ComponentType } from "react";
import { ShieldCheck, Wallet } from "lucide-react";
import { Tag, Boxes, Package, Box, List } from "lucide-react";

export const ROUTES = {
  login: "/login",
  register: "/register",
  Services: "/services",
  items: "/items",
  packages: "/packages",
  pricing: "/pricing",
  // Route still exists (AppRouter.tsx) and is directly navigable, but it's
  // no longer linked from the nav or used as any role's post-login landing
  // page — see roleHome.ts (admins now land on Services instead).
  dashboard: "/dashboard",
  event: "/event-mode",
  video: "/videoAssesment",
  lead_home: "/ld/",
  approval: "/approval",
  packageApproval: "/package-approval",
  lov: "/lov",
  portal: "/portal",
  portalReports: "/portal/reports",
  portalProfile: "/portal/profile",
  portalPayments: "/portal/payments",
  portalCommunity: "/portal/community",
  portalBizcalendar: "/portal/bizcalendar",
  portalRemedies: "/portal/remedies",
  portalDasha: "/portal/dasha",
  pendingApproval: "/pending-approval",
} as const;

type IconType = ComponentType<{ className?: string }>;

export interface NavItem {
  label: string;
  href: string;
  icon: IconType;
}

export interface NavSection {
  kind: "section";
  label: string;
  icon: IconType;
  items: NavItem[];
}

export interface NavLeaf extends NavItem {
  kind: "item";
}

export type NavEntry = NavLeaf | NavSection;

/**
 * Flat list — kept for any legacy consumers.
 * Prefer APP_NAV below.
 *
 * Trimmed 2026-07-16: Dashboard, Leads, Accounts, Invoices, and Tasks had
 * nav entries + ROUTES constants but no matching route in AppRouter.tsx —
 * clicking them fell through to the 404 page. Removed as dead/placeholder
 * tabs rather than leaving broken links in the admin nav. Approvals/
 * Package Approvals/LOV Tables were flattened out of the collapsible
 * "Admin" dropdown into plain top-level items, matching every other tab.
 */
export const APP_NAV_ITEMS: NavItem[] = [
  { label: "Services", href: ROUTES.Services, icon: Package },
  { label: "Items", href: ROUTES.items, icon: Box },
  { label: "Approvals", href: ROUTES.approval, icon: ShieldCheck },
  { label: "Package Approvals", href: ROUTES.packageApproval, icon: Wallet },
  { label: "LOV Tables", href: ROUTES.lov, icon: List },
  { label: "Packages", href: ROUTES.packages, icon: Boxes },
  { label: "Pricing", href: ROUTES.pricing, icon: Tag },
];

/**
 * Sectioned nav type is still used by Sidebar.tsx, but every entry below is
 * now a flat `kind: "item"` — no more collapsible "Admin" section.
 */
export const APP_NAV: NavEntry[] = [
  { kind: "item", label: "Services",          href: ROUTES.Services,        icon: Package },
  { kind: "item", label: "Items",              href: ROUTES.items,           icon: Box },
  { kind: "item", label: "Approvals",          href: ROUTES.approval,        icon: ShieldCheck },
  { kind: "item", label: "Package Approvals",  href: ROUTES.packageApproval, icon: Wallet },
  { kind: "item", label: "LOV Tables",         href: ROUTES.lov,             icon: List },
  { kind: "item", label: "Packages",           href: ROUTES.packages,        icon: Boxes },
  { kind: "item", label: "Pricing",            href: ROUTES.pricing,         icon: Tag },
];

export const APP_NAME = "BizPandit";
